import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import clsx from 'clsx';
import { Music24FeatureGroup } from '../../../config/music24PlanFeatureRows';
import { TypedMemo } from '../../../../common/types';
import { CompareTier } from './CompareTierSwitch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the compare table; each part uses its own slice of it */
import styles from './PricingCompareTable.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A folded group shows this many rows until the reader opens it. */
export const COMPARE_FOLDED_ROWS = 6;

const messages = defineMessages({
  count: { id: 'app.sml.plans.compare.rowCount', defaultMessage: '{count, plural, one {# row} other {# rows}}' },
  showMore: {
    id: 'app.sml.plans.compare.showMore',
    defaultMessage: '{count, plural, one {Show # more row} other {Show # more rows}}',
  },
  showFewer: { id: 'app.sml.plans.compare.showFewer', defaultMessage: 'Show fewer rows' },
});

export interface CompareGroupData {
  group: Music24FeatureGroup;
  label: string;
  rows: {
    key: string;
    label: string;
    cells: { tier: CompareTier; text: string; isNone: boolean }[];
  }[];
}

interface CompareGroupProps {
  data: CompareGroupData;
  /** The first group shows every row; the others fold after `COMPARE_FOLDED_ROWS`. */
  foldable: boolean;
  columnCount: number;
  /** The tint and the phone-width visibility of each column. */
  classNameOf: Map<CompareTier, string>;
}

/** One group of the compare table (Release, Promote, Measure, Team) as its own `<tbody>`. */
const CompareGroupComponent: React.FC<CompareGroupProps> = ({ data, foldable, columnCount, classNameOf }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const canFold = foldable && data.rows.length > COMPARE_FOLDED_ROWS;
  const rows = canFold && !open ? data.rows.slice(0, COMPARE_FOLDED_ROWS) : data.rows;
  const hiddenCount = data.rows.length - COMPARE_FOLDED_ROWS;

  return (
    <tbody>
      <tr className={styles.groupRow}>
        <td colSpan={columnCount}>
          <span className={styles.groupLabel}>{data.label}</span>
          <span className={styles.groupCount}>{intl.formatMessage(messages.count, { count: data.rows.length })}</span>
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.key} className={styles.dataRow}>
          <td className={styles.label}>{row.label}</td>
          {row.cells.map((cell) => (
            <td key={cell.tier} className={clsx(styles.value, cell.isNone && styles.none, classNameOf.get(cell.tier))}>
              {cell.text}
            </td>
          ))}
        </tr>
      ))}
      {canFold && (
        <tr className={styles.moreRow}>
          <td colSpan={columnCount}>
            <button type="button" className={styles.moreButton} onClick={() => setOpen((current) => !current)}>
              {open
                ? intl.formatMessage(messages.showFewer)
                : intl.formatMessage(messages.showMore, { count: hiddenCount })}
            </button>
          </td>
        </tr>
      )}
    </tbody>
  );
};

export const CompareGroup = TypedMemo(CompareGroupComponent);
